// App.jsx (React Router v5)
import "./App.css";
import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import GuestLayout from "./components/layouts/Guest/GuestLayout";
import { LazyMotion, domAnimation } from "framer-motion";
import i18n from "./i18n"; // make sure you've initialized i18n
import { ssr } from "./lib/ssrState";

// Helper to determine the latest selected language (defaults to 'en')
const getActiveLang = () => {
  const current =
    i18n.language ||
    (typeof window !== "undefined" ? localStorage.getItem("i18nextLng") : null);
  return current && current.startsWith("id") ? "id" : "en";
};

// HOC to sync :lang with i18n
const withLocale = (Component) => (props) => {
  const { lang } = props.match.params || {};
  useEffect(() => {
    if (lang && ["en", "id"].includes(lang)) i18n.changeLanguage(lang);
  }, [lang]);
  return <Component {...props} />;
};

const LangGuestLayout = withLocale(GuestLayout);
// Private area is never prerendered: load it on demand so public pages stay light.
const UserLayout = React.lazy(() => import("./components/layouts/User/UserLayout"));
const LangUserLayout = withLocale(UserLayout);

// Routes only (no router) so the same tree can be rendered by BrowserRouter
// in the browser and StaticRouter during build-time prerendering.
export function AppRoutes() {
  return (
    <LazyMotion features={domAnimation}>
    <Suspense fallback={null}>
      <Switch>
        {/* Dynamic redirects based on last selected language */}
        <Route
          exact
          path="/"
          render={({ location }) => (
            // keep ?gclid / ?fbclid etc. so ad click ids survive the language redirect
            <Redirect to={{ pathname: `/${getActiveLang()}`, search: location.search }} />
          )}
        />
        <Route
          exact
          path="/user"
          render={() => <Redirect to={`/${getActiveLang()}/user`} />}
        />

        {/* Locale-aware routes restricted to supported languages */}
        <Route path="/:lang(en|id)/user" component={LangUserLayout} />
        <Route path="/:lang(en|id)" component={LangGuestLayout} />

        {/* Fallback for unlocalized /article/x or other unmatched paths */}
        <Route render={() => <Redirect to={`/${getActiveLang()}`} />} />
      </Switch>
    </Suspense>
    </LazyMotion>
  );
}

function App() {
  // Hydration of prerendered HTML is done once this effect runs.
  useEffect(() => {
    ssr.prerender = false;
  }, []);

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
